import { memo, type ReactNode } from 'react';
import { tv } from 'tailwind-variants';
import { Switch } from './Switch.js';

const settingsToggleList = tv({
  base: 'grid gap-3',
  variants: {
    layout: {
      /** Default: 2-up, collapse to 1 below 960px. */
      pair: 'grid-cols-2 max-[960px]:grid-cols-1',
      /** Always single column. */
      column: 'grid-cols-1',
      /** Branding apply-to: 1 → 2 → 4 (sm / lg from shared BREAKPOINTS). */
      quad: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    },
  },
  defaultVariants: { layout: 'pair' },
});

const settingsCard = tv({
  base: [
    'relative grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 rounded-md p-4',
    'border-[1.5px] border-[var(--color-an-border)] bg-[var(--color-an-surface)]',
    'transition-[border-color,box-shadow,background-color] duration-150',
    'hover:border-[color-mix(in_srgb,var(--color-an-primary)_35%,var(--color-an-surface))]',
    'hover:bg-[var(--color-an-surface-hover)] hover:shadow-[var(--shadow-sm,0_1px_3px_rgba(15,23,42,0.08))]',
  ].join(' '),
  variants: {
    selected: {
      true: [
        'border-[color-mix(in_srgb,var(--color-an-primary)_45%,var(--color-an-surface))]',
        'bg-[color-mix(in_srgb,var(--color-an-primary)_6%,var(--color-an-surface))]',
        'shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-an-primary)_12%,transparent)]',
      ].join(' '),
      false: '',
    },
    disabled: {
      true: 'cursor-not-allowed opacity-[0.58] hover:border-[var(--color-an-border)] hover:bg-[var(--color-an-surface)] hover:shadow-none',
      false: '',
    },
    /** Summary owns message text — red border / aria only (FORM_SCRIPTS_12_9). */
    invalid: {
      true: [
        'border-[var(--color-an-danger)]',
        'bg-[color-mix(in_srgb,var(--color-an-danger)_6%,var(--color-an-surface))]',
        'hover:border-[var(--color-an-danger)]',
        'hover:bg-[color-mix(in_srgb,var(--color-an-danger)_8%,var(--color-an-surface))]',
      ].join(' '),
      false: '',
    },
  },
  defaultVariants: { selected: false, disabled: false, invalid: false },
});

const settingsCardTitle = tv({
  base: 'text-base font-semibold leading-[1.4] text-[var(--color-an-text)]',
});

const settingsCardHint = tv({
  base: 'text-sm leading-normal text-[var(--color-an-text-muted)]',
});

const settingsCardBody = tv({
  base: 'flex min-w-0 flex-col gap-[0.35rem]',
});

const settingsChoiceMark = tv({
  base: [
    'flex h-[1.45rem] w-[1.45rem] shrink-0 items-center justify-center rounded-full',
    'border-2 border-[var(--color-an-border)] transition-[border-color] duration-150',
  ].join(' '),
  variants: {
    selected: {
      true: 'border-[var(--color-an-primary)]',
      false: '',
    },
  },
  defaultVariants: { selected: false },
});

const settingsChoiceDot = tv({
  base: [
    'h-[0.55rem] w-[0.55rem] scale-0 rounded-full bg-transparent',
    'transition-[transform,background-color] duration-150',
  ].join(' '),
  variants: {
    selected: {
      true: 'scale-100 bg-[var(--color-an-primary)]',
      false: '',
    },
  },
  defaultVariants: { selected: false },
});

const visuallyHiddenInput = 'absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [-webkit-clip-path:inset(50%)] [clip-path:inset(50%)] [margin:-1px]';

const switchFocusRing = [
  'peer-focus-visible:outline',
  'peer-focus-visible:outline-2',
  'peer-focus-visible:outline-offset-2',
  'peer-focus-visible:outline-[var(--color-an-primary)]',
].join(' ');

export type SettingsToggleListLayout = 'pair' | 'column' | 'quad';

export interface SettingsToggleListProps {
  readonly children: ReactNode;
  /**
   * When true, forces a single column (same as `layout="column"`).
   * Ignored when `layout` is set explicitly.
   */
  readonly column?: boolean;
  /** Grid density. Defaults to `pair`, or `column` when `column` is true. */
  readonly layout?: SettingsToggleListLayout;
  readonly className?: string;
}

export const SettingsToggleList = memo(
  ({
    children,
    column = false,
    layout,
    className = '',
  }: SettingsToggleListProps): JSX.Element => {
    const resolvedLayout: SettingsToggleListLayout =
      layout ?? (column ? 'column' : 'pair');
    return (
      <div className={settingsToggleList({ layout: resolvedLayout, className })}>
        {children}
      </div>
    );
  },
);

SettingsToggleList.displayName = 'SettingsToggleList';

export interface SettingsToggleProps {
  readonly id: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  /** Invalid chrome only — message text stays on FormErrorSummary (no under-control copy). */
  readonly invalid?: boolean;
  readonly title: string;
  /** Optional control beside the title (e.g. muted MetricExplainButton). Outside checkbox label click path via stopPropagation on the control. */
  readonly titleEnd?: ReactNode;
  readonly hint?: string;
  /** Native browser tooltip (e.g. blocked-rail readiness reason). */
  readonly titleTooltip?: string;
  readonly onChange: (checked: boolean) => void;
  readonly className?: string;
  /** Forwarded onto the root `<label>` for Playwright / unit selectors. */
  readonly 'data-testid'?: string;
}

export const SettingsToggle = memo(
  ({
    id,
    checked,
    disabled = false,
    invalid = false,
    title,
    titleEnd,
    hint,
    titleTooltip,
    onChange,
    className = '',
    'data-testid': dataTestId,
  }: SettingsToggleProps): JSX.Element => (
    <label
      htmlFor={id}
      className={settingsCard({
        selected: checked,
        disabled,
        invalid,
        className: ['settings-toggle-card', className].filter(Boolean).join(' '),
      })}
      title={titleTooltip}
      data-testid={dataTestId}
    >
      <input
        id={id}
        type="checkbox"
        className={`peer ${visuallyHiddenInput}`}
        checked={checked}
        disabled={disabled}
        aria-invalid={invalid ? true : undefined}
        aria-label={title}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={settingsCardBody()}>
        <span className={`${settingsCardTitle()} inline-flex min-w-0 flex-wrap items-center gap-1`}>
          <span>{title}</span>
          {/* MetricExplainButton already stopPropagates click/pointerDown (avoids label toggle). */}
          {titleEnd != null ? titleEnd : null}
        </span>
        {hint ? <span className={settingsCardHint()}>{hint}</span> : null}
      </span>
      <Switch checked={checked} className={switchFocusRing} />
    </label>
  ),
);

SettingsToggle.displayName = 'SettingsToggle';

export interface SettingsChoiceCardProps {
  readonly id: string;
  readonly name: string;
  readonly value: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  /** Invalid chrome only — message text stays on FormErrorSummary (no under-control copy). */
  readonly invalid?: boolean;
  readonly title: string;
  /** Optional control beside the title (e.g. muted MetricExplainButton). Outside radio label text click path via stopPropagation on the control. */
  readonly titleEnd?: ReactNode;
  readonly hint?: string;
  readonly onChange: (value: string) => void;
  readonly className?: string;
  /** Forwarded onto the root `<label>` for Playwright / unit selectors. */
  readonly 'data-testid'?: string;
}

export const SettingsChoiceCard = memo(
  ({
    id,
    name,
    value,
    checked,
    disabled = false,
    invalid = false,
    title,
    titleEnd,
    hint,
    onChange,
    className = '',
    'data-testid': dataTestId,
  }: SettingsChoiceCardProps): JSX.Element => (
    <label
      htmlFor={id}
      className={settingsCard({
        selected: checked,
        disabled,
        invalid,
        className: ['settings-choice-card', className].filter(Boolean).join(' '),
      })}
      data-testid={dataTestId}
      aria-disabled={disabled ? true : undefined}
      aria-invalid={invalid ? true : undefined}
    >
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        className={`peer ${visuallyHiddenInput}`}
        checked={checked}
        disabled={disabled}
        aria-label={title}
        onChange={() => onChange(value)}
      />
      <span className={settingsCardBody()}>
        <span className={`${settingsCardTitle()} inline-flex min-w-0 flex-wrap items-center gap-1`}>
          <span>{title}</span>
          {titleEnd != null ? titleEnd : null}
        </span>
        {hint ? <span className={settingsCardHint()}>{hint}</span> : null}
      </span>
      <span className={`${settingsChoiceMark({ selected: checked })} ${switchFocusRing}`} aria-hidden="true">
        <span className={settingsChoiceDot({ selected: checked })} />
      </span>
    </label>
  ),
);

SettingsChoiceCard.displayName = 'SettingsChoiceCard';
